/**
 * звук озера: два тона 180 и 188 Гц, разница 8 Гц.
 * это звуковое сопровождение практики, не лечение и не изменение мозговых ритмов.
 * если StereoPanner недоступен, режим не называется бинауральным.
 *
 * Жизненный цикл (исправление P2 из аудита):
 * — stop() завершает только тот контекст, который сам открыл;
 * — повторный start() сначала гасит предыдущий;
 * — отложенное закрытие не трогает пришедший на замену контекст.
 */

import { messages, type Messages } from '../../i18n/index.js';

export interface GainLike {
  gain: { value: number; linearRampToValueAtTime(v: number, t: number): void };
  connect(node: unknown): unknown;
}

export interface OscillatorLike {
  frequency: { value: number };
  type: string;
  connect(node: unknown): unknown;
  start(): void;
  stop(): void;
}

export interface PannerLike {
  pan: { value: number };
  connect(node: unknown): unknown;
}

export interface AudioContextLike {
  currentTime: number;
  state: string;
  destination: unknown;
  createOscillator(): OscillatorLike;
  createGain(): GainLike;
  createStereoPanner?: () => PannerLike;
  resume?: () => Promise<void>;
  close(): Promise<void> | void;
}

export type AudioContextFactory = () => AudioContextLike;

export type ScheduleFn = (cb: () => void, ms: number) => unknown;
export type CancelFn = (handle: unknown) => void;

export interface BinauralOptions {
  createContext: AudioContextFactory;
  schedule?: ScheduleFn;
  cancel?: CancelFn;
  baseHz?: number;
  beatHz?: number;
  peakGain?: number;
  fadeInSec?: number;
  fadeOutSec?: number;
  /** сколько ждать после затухания перед закрытием контекста */
  closeDelayMs?: number;
  /** причины отказа на языке человека; без них — русские */
  texts?: SoundTexts;
}

type SoundTexts = Messages['lake']['sound'];

export type AudioStatus =
  | { kind: 'off' }
  | { kind: 'starting' }
  | { kind: 'playing'; stereo: boolean }
  | { kind: 'failed'; message: string };

interface Live {
  ctx: AudioContextLike;
  nodes: { osc: OscillatorLike; gain: GainLike }[];
  closeHandle: unknown;
}

export class BinauralEngine {
  private live: Live | null = null;
  private readonly opts: Required<Omit<BinauralOptions, 'createContext' | 'schedule' | 'cancel' | 'texts'>> & {
    createContext: AudioContextFactory;
    schedule: ScheduleFn;
    cancel: CancelFn;
    texts: SoundTexts;
  };

  constructor(options: BinauralOptions) {
    this.opts = {
      createContext: options.createContext,
      schedule: options.schedule ?? ((cb, ms) => setTimeout(cb, ms)),
      cancel: options.cancel ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>)),
      baseHz: options.baseHz ?? 180,
      beatHz: options.beatHz ?? 8,
      peakGain: options.peakGain ?? 0.06,
      fadeInSec: options.fadeInSec ?? 3,
      fadeOutSec: options.fadeOutSec ?? 1,
      closeDelayMs: options.closeDelayMs ?? 1300,
      texts: options.texts ?? messages().lake.sound,
    };
  }

  get isPlaying(): boolean {
    return this.live !== null;
  }

  /** сколько контекстов сейчас открыто — для проверок в тестах */
  get openContexts(): number {
    return this.live ? 1 : 0;
  }

  async start(): Promise<AudioStatus> {
    // повторный запуск сначала завершает предыдущий
    this.stop();

    let ctx: AudioContextLike;
    try {
      ctx = this.opts.createContext();
    } catch (err) {
      return { kind: 'failed', message: describe(err, this.opts.texts) };
    }

    try {
      // на iOS контекст стартует suspended: resume нужен после жеста пользователя
      if (ctx.state === 'suspended' && ctx.resume) await ctx.resume();
    } catch (err) {
      try {
        await ctx.close();
      } catch {
        // контекст уже мёртв
      }
      return { kind: 'failed', message: describe(err, this.opts.texts) };
    }

    // пока ждали resume, мог прийти новый start — тогда этот контекст лишний
    if (this.live !== null) {
      try {
        await ctx.close();
      } catch {
        // уже закрыт
      }
      return { kind: 'failed', message: this.opts.texts.superseded };
    }

    const nodes: Live['nodes'] = [];
    let stereo = true;
    try {
      for (const [idx, sidePan] of [-1, 1].entries()) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        const panner = ctx.createStereoPanner ? ctx.createStereoPanner() : null;
        if (!panner) stereo = false;

        osc.frequency.value = this.opts.baseHz + (idx === 1 ? this.opts.beatHz : 0);
        osc.type = 'sine';
        gain.gain.value = 0;
        gain.gain.linearRampToValueAtTime(
          this.opts.peakGain,
          ctx.currentTime + this.opts.fadeInSec,
        );
        osc.connect(gain);
        if (panner) {
          panner.pan.value = sidePan;
          gain.connect(panner);
          panner.connect(ctx.destination);
        } else {
          gain.connect(ctx.destination);
        }
        osc.start();
        nodes.push({ osc, gain });
      }
    } catch (err) {
      for (const n of nodes) {
        try {
          n.osc.stop();
        } catch {
          // осциллятор мог не стартовать
        }
      }
      try {
        await ctx.close();
      } catch {
        // уже закрыт
      }
      return { kind: 'failed', message: describe(err, this.opts.texts) };
    }

    this.live = { ctx, nodes, closeHandle: null };
    return { kind: 'playing', stereo };
  }

  /** гасит текущий звук; закрывается ровно тот контекст, который здесь захвачен */
  stop(): void {
    const live = this.live;
    if (!live) return;
    this.live = null;

    const { ctx, nodes } = live;
    try {
      for (const n of nodes) {
        n.gain.gain.linearRampToValueAtTime(0, ctx.currentTime + this.opts.fadeOutSec);
      }
    } catch {
      // узлы уже отсоединены
    }

    this.opts.schedule(() => {
      for (const n of nodes) {
        try {
          n.osc.stop();
        } catch {
          // уже остановлен
        }
      }
      try {
        void ctx.close();
      } catch {
        // уже закрыт
      }
    }, this.opts.closeDelayMs);
  }

  /** немедленное завершение без затухания — для unmount */
  dispose(): void {
    const live = this.live;
    this.live = null;
    if (!live) return;
    for (const n of live.nodes) {
      try {
        n.osc.stop();
      } catch {
        // уже остановлен
      }
    }
    try {
      void live.ctx.close();
    } catch {
      // уже закрыт
    }
  }
}

function describe(err: unknown, texts: SoundTexts): string {
  if (err instanceof Error && err.message) return err.message;
  return texts.unavailable;
}

export function browserAudioFactory(texts: SoundTexts = messages().lake.sound): AudioContextFactory {
  return () => {
    const w = window as unknown as {
      AudioContext?: new () => AudioContextLike;
      webkitAudioContext?: new () => AudioContextLike;
    };
    const Ctor = w.AudioContext ?? w.webkitAudioContext;
    if (!Ctor) throw new Error(texts.noWebAudio);
    return new Ctor();
  };
}
